<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold">Salida de Garantía</h1>
        <p class="text-subtitle-1 grey--text">
          Registrar devolución de productos en garantía
        </p>
      </v-col>
    </v-row>

    <!-- Store and Client Info Cards -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card class="info-card elevation-0" outlined height="100%">
          <v-card-title>
            <div class="info-icon">
              <v-icon color="primary" size="28">mdi-store</v-icon>
            </div>
            <h3 class="ml-3">{{ store.store_name }}</h3>
          </v-card-title>
          <v-card-text>
            <div class="info-content">
              <div class="info-item">
                <span class="info-label">Dirección:</span>
                <span class="info-value">{{ store.store_address }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ciudad:</span>
                <span class="info-value">{{ store.store_city }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="info-card elevation-0" outlined height="100%">
          <v-card-title>
            <div class="info-icon mr-3">
              <v-icon color="orange" size="28">mdi-account-arrow-right</v-icon>
            </div>
            <h3>Entregar a</h3>
            <v-spacer></v-spacer>
            <v-btn icon @click="client_dialog = !client_dialog">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-5">
            <div class="info-content" v-if="client.PartyName">
              <div class="info-item">
                <span class="info-label">ID:</span>
                <span class="info-value font-weight-medium">{{
                  client.PartyIdentification
                }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Nombre:</span>
                <span class="info-value">{{ client.PartyName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Email:</span>
                <span class="info-value">{{ client.client_email || "No definido" }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Teléfono:</span>
                <span class="info-value">{{ client.client_phone || "No definido" }}</span>
              </div>
            </div>
            <div v-else class="text-center">
              <v-icon size="48" color="grey lighten-2">mdi-account-question</v-icon>
              <p class="grey--text mt-2 mb-0">No se ha seleccionado un cliente</p>
              <v-btn
                text
                small
                color="primary"
                @click="client_dialog = true"
                class="mt-2"
              >
                <v-icon left small>mdi-account-plus</v-icon>
                Editar Cliente
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Products Table -->
    <v-card class="main-card elevation-0 mb-6" outlined>
      <v-card-title>
        <v-icon color="orange" class="mr-3">mdi-shield-check</v-icon>
        <h3 class="section-title">Productos en Garantía</h3>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="product_dialog = !product_dialog">
          <v-icon left>mdi-magnify</v-icon>
          Buscar Productos
        </v-btn>
      </v-card-title>

      <!-- Desktop Table -->
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <v-data-table
          :headers="headers_items"
          :items="items"
          :items-per-page="10"
          item-key="ean"
          class="minimal-table"
          :loading="loading_status"
          loading-text="Cargando productos..."
          loader-height="3"
          hide-default-footer
          v-show="items.length > 0"
        >
          <template v-slot:[`item.producto`]="{ item }">
            <div class="product-info">
              <div class="product-main font-weight-medium">
                {{ item.brand || item.marca }} {{ item.refe }}
              </div>
              <div class="product-details text-caption grey--text">
                {{ item.colorName || item.color }} - {{ item.talla }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.categoria_grupo`]="{ item }">
            <div>
              <v-chip 
                small 
                label 
                class="mr-1" 
                color="orange"
                :title="get_categoria_name(item.category || item.categoria)"
              >
                {{ get_categoria_name(item.category || item.categoria) }}
              </v-chip>
              <v-chip 
                small 
                label 
                outlined
                color="secondary"
                :title="get_grupo_name(item.groupName || item.grupo)"
              >
                {{ get_grupo_name(item.groupName || item.grupo) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.valores`]="{ item }">
            <div class="values-info text-right">
              <div class="value-price font-weight-medium">
                {{ formatCurrency(item.precio) }}
              </div>
              <div
                class="value-subtotal text-caption grey--text"
                v-if="item.descuento > 0"
              >
                Desc: {{ item.descuento }}%
              </div>
            </div>
          </template>

          <template v-slot:[`item.cantidad`]="{ item }">
            <div class="text-center">
              <v-chip color="orange" small> {{ item.cantidad }} und </v-chip>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="error" @click="delete_item(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- Footer with totals -->
          <template v-slot:body.append v-if="items.length > 0">
            <tr class="orange lighten-5">
              <td colspan="3" class="text-right font-weight-bold pa-4">TOTAL</td>
              <td class="text-center font-weight-bold">{{ totalQuantity }} und</td>
              <td class="text-right font-weight-bold">
                {{ formatCurrency(totalValue) }}
              </td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </template>

      <!-- Mobile List -->
      <template v-else>
        <v-list v-if="items.length > 0" class="py-0">
          <template v-for="(item, index) in items">
            <v-list-item :key="item.ean" class="px-4 py-3">
              <v-list-item-content>
                <div class="d-flex justify-space-between align-start mb-2">
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">
                      {{ item.brand || item.marca }} {{ item.refe }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.colorName || item.color }} - {{ item.talla }}
                    </div>
                  </div>
                  <v-btn icon small color="error" @click="delete_item(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>

                <div class="d-flex justify-space-between align-center">
                  <div>
                    <v-chip 
                      small 
                      label 
                      class="mr-1"
                      color="orange"
                      :title="get_categoria_name(item.category || item.categoria)"
                    >
                      {{ get_categoria_name(item.category || item.categoria) }}
                    </v-chip>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-medium">
                      {{ formatCurrency(item.precio) }}
                    </div>
                    <div class="text-caption">{{ item.cantidad }} und</div>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index < items.length - 1" :key="`div-${item.ean}`" />
          </template>

          <!-- Mobile Totals -->
          <v-list-item class="orange lighten-5 py-3">
            <v-list-item-content>
              <div class="d-flex justify-space-between align-center">
                <div class="font-weight-bold">TOTAL</div>
                <div class="text-right">
                  <div class="font-weight-bold">{{ formatCurrency(totalValue) }}</div>
                  <div class="text-caption">{{ totalQuantity }} unidades</div>
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <!-- Empty State -->
      <div v-if="items.length === 0 && !loading_status" class="text-center py-12">
        <v-icon size="64" color="grey lighten-2">mdi-shield-off</v-icon>
        <h3 class="text-h6 mt-4 grey--text">No hay productos agregados</h3>
        <p class="grey--text mt-2">Busca productos en garantía para devolver</p>
      </div>
    </v-card>

    <!-- Signature and Motive -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card class="main-card elevation-0" outlined height="100%">
          <v-card-title>
            <v-icon color="info" class="mr-3">mdi-draw</v-icon>
            <h3 class="section-title">Firma del Cliente</h3>
          </v-card-title>
          <v-card-text>
            <VueSignaturePad
              class="signature-pad"
              width="100%"
              height="200px"
              ref="signaturePad"
            />
            <v-btn text small color="error" @click="clearSignature" class="mt-2">
              <v-icon left small>mdi-eraser</v-icon>
              Limpiar Firma
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="main-card elevation-0" outlined height="100%">
          <v-card-title>
            <v-icon color="warning" class="mr-3">mdi-text-box-outline</v-icon>
            <h3 class="section-title">Concepto de Salida</h3>
          </v-card-title>
          <v-card-text>
            <v-form
              v-model="validatemotive"
              ref="motiveform"
              @submit.prevent="save_receipt"
            >
              <v-select
                v-model="motive"
                :items="FP"
                item-value="codigo"
                item-text="valor"
                return-object
                outlined
                :rules="f_required"
                label="Seleccionar motivo de salida"
                prepend-inner-icon="mdi-format-list-bulleted"
              />
              <v-btn
                x-large
                color="orange"
                type="submit"
                block
                :disabled="!canSubmit"
                :loading="loading_status"
                class="mt-4"
              >
                <v-icon left>mdi-check</v-icon>
                Registrar Salida
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Client Dialog -->
    <v-dialog v-model="client_dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="primary">
            <v-icon class="mr-3">mdi-account-edit</v-icon>
            <v-toolbar-title>Editar Cliente</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="client_dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="client.typeid"
                :items="id_lst"
                item-value="code"
                item-text="name"
                label="Tipo de documento"
                outlined
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="client.PartyIdentification"
                label="Número de documento"
                ref="idNumber"
                outlined
                @keyup.enter="validateID"
                autofocus
                clearable
                prepend-inner-icon="mdi-numeric"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="client.PartyName"
                label="Nombre Completo"
                outlined
                prepend-inner-icon="mdi-account"
              />
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="client.AddressLine"
                label="Dirección"
                outlined
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="client.CityName"
                label="Ciudad"
                outlined
                prepend-inner-icon="mdi-city"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="client_dialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveClient">
            <v-icon left>mdi-check</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Search Products Dialog -->
    <v-dialog v-model="product_dialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="primary">
            <v-icon class="mr-3">mdi-magnify</v-icon>
            <v-toolbar-title>Buscar Productos en Garantía</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="product_dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-form v-model="searchvalid" ref="searchform" @submit.prevent="get_report">
            <v-container fluid class="pa-4 grey lighten-5">
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="issueDate"
                    label="Garantías desde"
                    outlined
                    dense
                    type="date"
                    :rules="f_required"
                    prepend-inner-icon="mdi-calendar"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="search"
                    :items="searchOptions"
                    item-value="code"
                    item-text="name"
                    label="Buscar por"
                    outlined
                    dense
                    :rules="f_required"
                    prepend-inner-icon="mdi-filter"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="searcText"
                    label="Valor de búsqueda"
                    outlined
                    dense
                    autofocus
                    clearable
                    :rules="f_required"
                    prepend-inner-icon="mdi-magnify"
                    @keyup.enter="get_report"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-btn
                    block
                    color="primary"
                    large
                    type="submit"
                    :loading="loading_status"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Buscar
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>

          <v-data-table
            :headers="headers"
            :items="concepts"
            :items-per-page="10"
            item-key="id"
            class="minimal-table"
            :loading="loading_status"
            loader-height="3"
            loading-text="Buscando productos..."
          >
            <template v-slot:[`item.info`]="{ item }">
              <div class="product-search-info">
                <div class="font-weight-medium">
                  {{ item.brand || item.marca }} - {{ item.refe }}
                </div>
                <div class="text-caption grey--text">
                  {{ item.colorName || item.color }} - {{ item.talla }}
                </div>
                <div class="mt-1">
                  <v-chip 
                    x-small 
                    label 
                    class="mr-1" 
                    color="orange"
                    :title="get_categoria_name(item.category || item.categoria)"
                  >
                    {{ get_categoria_name(item.category || item.categoria) }}
                  </v-chip>
                  <v-chip 
                    x-small 
                    label 
                    outlined
                    color="secondary"
                    :title="get_grupo_name(item.groupName || item.grupo)"
                  >
                    {{ get_grupo_name(item.groupName || item.grupo) }}
                  </v-chip>
                </div>
              </div>
            </template>

            <template v-slot:[`item.fecha`]="{ item }">
              <div class="text-caption">{{ item.order_date }}</div>
            </template>

            <template v-slot:[`item.precio`]="{ item }">
              <div class="text-right">
                <div class="font-weight-medium">{{ formatCurrency(item.precio) }}</div>
                <div class="text-caption grey--text" v-if="item.descuento > 0">
                  Desc: {{ item.descuento }}%
                </div>
              </div>
            </template>

            <template v-slot:[`item.stock`]="{ item }">
              <div class="text-center">
                <v-chip
                  small
                  :color="item.cantidad > 0 ? 'success' : 'error'"
                  text-color="white"
                >
                  {{ item.cantidad }} und
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                color="primary"
                @click="add2concepts(item)"
                :disabled="item.cantidad <= 0"
              >
                <v-icon left>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Print Drawer -->
    <v-navigation-drawer
      v-model="showPrint"
      right
      temporary
      width="400"
      class="print-drawer"
    >
      <v-card height="100%" flat>
        <v-card-title class="pa-0">
          <v-toolbar dark color="primary">
            <v-icon class="mr-3">mdi-printer</v-icon>
            <v-toolbar-title>Vista Previa - Salida Garantía</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="showPrint = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-img :src="dataURL" contain></v-img>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn block large color="primary" @click="print_doc">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import createClient from "@/utils/createClient.js";

export default {
  name: "WarrantyOut",
  data() {
    return {
      // UI State
      searchvalid: false,
      validatemotive: false,
      client_dialog: false,
      product_dialog: false,
      loading_status: false,
      showPrint: false,

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Search
      search: "client_id",
      searcText: "",
      issueDate: getToday(),
      searchOptions: [
        { code: "client_id", name: "Cédula del Cliente" },
        { code: "order_number", name: "Número de Documento" },
        { code: "client_phone", name: "Teléfono del Cliente" },
      ],

      // Data
      concepts: [],
      items: [],
      motive: { codigo: null, valor: null },
      dataURL: "",
      signature: null,

      // Store and Client
      store: createClient(),
      client: createClient(),

      // Totals
      totals: { qty: 0, total: 0 },

      // Lists
      FP: [],
      id_lst: [
        { code: "13", name: "Cédula de ciudadanía" },
        { code: "31", name: "NIT" },
        { code: "11", name: "Registro civil" },
        { code: "12", name: "Tarjeta de identidad" },
        { code: "21", name: "Tarjeta de extranjería" },
        { code: "22", name: "Cédula de extranjería" },
        { code: "41", name: "Pasaporte" },
        { code: "42", name: "Documento de identificación extranjero" },
        { code: "50", name: "NIT de otro país" },
      ],

      // Headers for search
      headers: [
        {
          text: "Producto",
          value: "info",
          sortable: false,
          width: "35%",
        },
        {
          text: "Fecha",
          value: "fecha",
          align: "center",
          width: "15%",
        },
        {
          text: "Precio",
          value: "precio",
          align: "right",
          width: "15%",
        },
        {
          text: "Stock",
          value: "stock",
          align: "center",
          width: "15%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "20%",
        },
      ],

      // Headers for items
      headers_items: [
        {
          text: "Producto",
          value: "producto",
          sortable: false,
          width: "35%",
        },
        {
          text: "Categoría / Grupo",
          value: "categoria_grupo",
          sortable: false,
          width: "25%",
        },
        {
          text: "Cantidad",
          value: "cantidad",
          align: "center",
          width: "15%",
        },
        {
          text: "Valor",
          value: "valores",
          align: "right",
          width: "20%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "5%",
        },
      ],

      // Validation
      f_required: [(v) => !!v || "Campo requerido"],
    };
  },

  computed: {
    totalQuantity() {
      return this.items.reduce((sum, item) => sum + (parseFloat(item.cantidad) || 0), 0);
    },

    totalValue() {
      return this.items.reduce((sum, item) => {
        const precio = parseFloat(item.precio) || 0;
        const cantidad = parseFloat(item.cantidad) || 0;
        return sum + precio * cantidad;
      }, 0);
    },

    canSubmit() {
      return (
        this.motive.codigo &&
        this.items.length > 0 &&
        this.client.PartyName &&
        this.hasSignature()
      );
    },
  },

  mounted() {
    console.log(window.settings);
    this.store = window.store;
    this.FP = window.settings["G-SALIDA"];
    this.client = {
      PartyName: null,
      PartyIdentification: null,
      client_email: null,
      client_phone: null,
    };
  },

  methods: {
    // Utilities
    formatCurrency,

    showMessage(text, color = "info") {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout: 3000,
      };
    },

    get_categoria_name(codigo) {
      if (!codigo) return "";
      // Buscar en window.settings["CATEGORIA"] que es un array
      var categorias = window.settings && window.settings["CATEGORIA"] 
        ? window.settings["CATEGORIA"].find(item => item.codigo === codigo)
        : null;
      // Devolver el valor si existe y es diferente del código
      return categorias && categorias.valor && categorias.valor !== codigo 
        ? categorias.valor : codigo;
    },

    get_grupo_name(codigo) {
      if (!codigo) return "";
      // Buscar en window.settings["GRUPO"] que es un array
      var grupos = window.settings && window.settings["GRUPO"] 
        ? window.settings["GRUPO"].find(item => item.codigo === codigo)
        : null;
      // Devolver el valor si existe y es diferente del código
      return grupos && grupos.valor && grupos.valor !== codigo 
        ? grupos.valor : codigo;
    },

    // Signature methods
    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }
    },

    hasSignature() {
      if (this.$refs.signaturePad) {
        return !this.$refs.signaturePad.isEmpty();
      }
      return false;
    },

    getSignature() {
      if (this.$refs.signaturePad && !this.$refs.signaturePad.isEmpty()) {
        return this.$refs.signaturePad.saveSignature();
      }
      return null;
    },

    // Client Management
    validateID(e) {
      if (e.keyCode === 13 && this.client.PartyIdentification) {
        this.loading_status = true;

        const qry = {
          store: window.store.store_id,
          PartyIdentification: this.client.PartyIdentification,
        };

        webserver("get_client_info", qry, (data) => {
          this.loading_status = false;
          if (data) {
            this.client = data;
            this.showMessage("Cliente encontrado", "success");
          } else {
            this.showMessage("Cliente no encontrado", "warning");
          }
        });
      }
    },

    saveClient() {
      if (!this.client.PartyName || !this.client.PartyIdentification) {
        this.showMessage("Complete los datos del cliente", "warning");
        return;
      }

      this.client_dialog = false;
      this.showMessage("Cliente actualizado", "success");
    },

    // Product Management
    delete_item(item) {
      const index = this.items.indexOf(item);
      if (index !== -1) {
        this.items.splice(index, 1);
        this.showMessage("Producto eliminado", "info");
      }
    },

    add2concepts(item) {
      if (item.cantidad <= 0) {
        this.showMessage("Producto sin stock disponible", "error");
        return;
      }

      delete item.order_date;
      item.cantidad = 1;
      const newItem = { ...item };

      const index = this.items.findIndex((itm) => itm.ean === newItem.ean);
      if (index === -1) {
        this.items.push(newItem);
      } else {
        if (this.items[index].cantidad + 1 > item.cantidad) {
          this.showMessage("No hay suficiente stock disponible", "warning");
          return;
        }
        this.items[index].cantidad += 1;
      }

      this.product_dialog = false;
      this.showMessage("Producto agregado correctamente", "success");
    },

    // Search warranty products
    get_report(e) {
      if (e) e.preventDefault();

      this.$refs.searchform.validate();

      if (!this.searchvalid) {
        this.showMessage("Complete todos los campos de búsqueda", "warning");
        return;
      }

      this.loading_status = true;

      const qry = {
        store: window.store.store_id,
        table: "items",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.store.store_id,
          },
          {
            field: "cantidad",
            operator: ">",
            value: 0,
          },
          {
            field: "categoria",
            operator: "=",
            value: "GARANTIAS",
          },
        ],
      };

      webserver("get_table", qry, (data) => {
        this.loading_status = false;
        this.concepts = data || [];

        if (data.length === 0) {
          this.showMessage("No se encontraron productos en garantía", "info");
        } else {
          this.showMessage(`${data.length} productos encontrados`, "success");
        }
      });
    },

    // Save Receipt
    save_receipt(e) {
      if (e) e.preventDefault();

      this.$refs.motiveform.validate();

      if (!this.canSubmit) {
        let missingField = "";
        if (!this.motive.codigo) missingField = "el concepto";
        else if (this.items.length === 0) missingField = "productos";
        else if (!this.client.PartyName) missingField = "el cliente";
        else if (!this.hasSignature()) missingField = "la firma";

        this.showMessage(`Debe completar: ${missingField}`, "warning");
        return;
      }

      this.loading_status = true;

      // Get signature
      this.signature = this.getSignature();

      const qry = {
        doc: {
          doc_type: "G-OUT",
          doc_value: this.totalValue,
          store: window.store.store_id,
          userId: window.profile.user_email,
        },
        items: this.items,
        paidTo: this.client,
        seller: window.seller,
        store: window.store.store_id,
        signature: this.signature,
        payments: [
          {
            payment_code: this.motive.codigo,
            payment_concept: this.motive.valor,
            payment_reference: "",
            payment_value: this.totalValue,
          },
        ],
      };

      webserver("put_stock", qry, (data) => {
        this.loading_status = false;
        this.resetDoc();
        this.print_receipt(data);
        this.showMessage("Salida de garantía registrada exitosamente", "success");
      });
    },

    resetDoc() {
      this.motive = { codigo: null, valor: null };
      this.items = [];
      this.client = createClient();
      this.clearSignature();
    },

    // Printing
    print_doc() {
      const printWindow = window.open("", "", "height=600, width=400");
      printWindow.document.write('<img src="' + this.dataURL + '" width="100%">');
      printWindow.print();
      printWindow.document.close();
      setTimeout(() => {
        printWindow.close();
      }, 500);
    },

    print_receipt(data) {
      const LS = 50;
      const itemsQty = data.items.length * LS * 3;
      const elem = document.createElement("canvas");
      elem.width = 945;
      elem.height = 1800 + itemsQty;

      const center = elem.width / 2;
      const left = 10;
      let line = 150;
      const right = elem.width - left;
      const ctx = elem.getContext("2d");

      ctx.font = "bold 40px Arial";
      ctx.textAlign = "center";

      ctx.font = "bold 60px Arial";
      ctx.fillText(window.seller.PartyIdentification_Id, center, line);
      line += LS;
      ctx.font = "bold 40px Arial";
      ctx.fillText(window.seller.PartyName, center, line);
      line += LS;
      ctx.fillText("Nit : " + window.seller.PartyIdentification, center, line);

      line += LS;
      ctx.fillText(
        window.seller.AddressLine + ", " + window.seller.CityName,
        center,
        line
      );
      line += LS;
      ctx.font = "bold 35px Arial";
      ctx.fillText("SALIDA DE GARANTÍA", center, line);
      ctx.font = "bold 40px Arial";
      line += LS;
      ctx.fillText("No. " + data.document.doc_number, center, line);
      line += LS;
      ctx.fillText(data.document.issueDate, center, line);
      line += LS;
      ctx.textAlign = "start";
      ctx.fillText("Cliente: " + data.buyer.PartyName, left, line);
      line += LS;
      ctx.fillText("C.C./Nit: " + data.buyer.PartyIdentification, left, line);
      line += LS;
      line += LS;
      ctx.font = "bold 40px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Productos", center, line);
      line += LS;
      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;

      data.items.forEach((row) => {
        const price = parseFloat(row.precio) * parseFloat(row.cantidad);
        const description =
          (row.brand || row.marca) +
          "-" +
          row.refe +
          "-" +
          (row.colorName || row.color) +
          "-" +
          row.talla;
        ctx.textAlign = "start";
        ctx.fillText(description, left, line);
        ctx.textAlign = "end";
        ctx.fillText(price.toLocaleString(), right, line);
        line += LS;
      });

      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;

      line += LS;

      ctx.textAlign = "center";
      ctx.fillText("Concepto de Salida", center, line);
      line += LS;

      data.payments.forEach((row) => {
        ctx.textAlign = "start";
        ctx.fillText(row.payment_concept, left, line);
        ctx.textAlign = "end";
        ctx.fillText(parseInt(row.payment_value).toLocaleString(), right, line);
        line += LS;
      });

      line += LS;

      // Add signature if available
      if (this.signature) {
        ctx.textAlign = "center";
        ctx.fillText("Firma del Cliente", center, line);
        line += LS;
        // Here you would draw the signature image
        // For now, just a placeholder
        ctx.fillText("_____________________", center, line);
      }

      line += LS;
      line += LS;

      this.dataURL = elem.toDataURL();
      this.showPrint = true;
    },
  },
};
</script>

<style scoped>
/* Info Cards */
.info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.info-icon {
  background-color: #f5f5f5;
  border-radius: 12px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 0.875rem;
  color: #666;
  min-width: 80px;
}

.info-value {
  font-size: 0.875rem;
  color: #333;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-main {
  color: #333;
}

.product-details {
  color: #666;
}

.product-search-info {
  padding: 8px 0;
}

/* Values */
.values-info {
  min-width: 120px;
}

.value-price {
  color: #333;
}

.value-subtotal {
  color: #666;
}

/* Signature */
.signature-pad {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

/* Print Drawer */
.print-drawer .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 3px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .section-title {
    font-size: 1.125rem;
  }

  .minimal-toolbar {
    padding: 12px 16px;
  }

  .info-card .v-card__text {
    padding: 16px !important;
  }
}

@media (max-width: 600px) {
  .section-title {
    font-size: 1rem;
  }

  .minimal-toolbar {
    padding: 12px;
  }

  .info-item {
    flex-wrap: wrap;
  }

  .info-label {
    min-width: 100%;
    margin-bottom: 4px;
  }
}
</style>
